/**
 * Algolia configuration for storefront-multibrand.
 *
 * Index naming convention (from breitling-storefront-next):
 * - Products: {environment}.PRODUCT.{channel}
 * - Stores: {environment}.STORE
 * - Service Centers: {environment}.SERVICE_CENTER
 *
 * IMPORTANT: The Algolia index is based on CHANNEL (country-currency), NOT language.
 * Example: PRE.PRODUCT.ca-cad is the same index for ca-en, ca-fr, and ca-zh.
 *
 * The language only affects:
 * 1. Localized facet attributes (e.g., case.FR, dial_color.EN_US)
 * 2. Which translation file to use for UI
 *
 * For testing, we use breitling's indexes until Gallet has its own.
 */

import { localeConfig } from '@/lib/configuration/configuration';

/**
 * Get the channel slug from a locale string.
 * Uses the centralized locale configuration.
 *
 * Channel format: {country_code}-{currency_code}
 * e.g., us-usd, gb-gbp, de-eur, ca-cad
 *
 * @param locale - Router locale like 'ca-fr', 'us-en'
 * @returns Channel slug like 'ca-cad', 'us-usd'
 */
function getChannelFromLocale(locale: string): string {
  // Try to get from configuration first
  try {
    return localeConfig.getChannelSlug(locale);
  } catch {
    // Fallback to simple country extraction if config lookup fails
    const country = locale.split('-')[0].toLowerCase();

    // Fallback channel map for countries not in config
    const fallbackChannelMap: Record<string, string> = {
      us: 'us-usd',
      ca: 'ca-cad',
      gb: 'gb-gbp',
      de: 'de-eur',
      fr: 'fr-eur',
      it: 'it-eur',
      es: 'es-eur',
      ch: 'ch-chf',
      jp: 'jp-jpy',
      kr: 'kr-krw',
      cn: 'cn-cny',
      hk: 'hk-hkd',
      tw: 'tw-twd',
      sg: 'sg-sgd',
      au: 'au-aud',
      nz: 'nz-nzd',
      ae: 'ae-aed',
      sa: 'sa-sar',
      za: 'za-zar',
    };

    return fallbackChannelMap[country] || 'us-usd';
  }
}

/**
 * Get the locale code for Algolia attribute lookups.
 * Converts locale string to the format used for localized facet attributes.
 *
 * NOTE: The Algolia INDEX is based on channel (country-currency), e.g., PRE.PRODUCT.ca-cad.
 * This function returns the locale code used for FACET ATTRIBUTES like:
 * - case.EN_US, case.FR, case.DE
 * - dial_color.ZH_HANS, dial_color.EN_GB
 *
 * Uses centralized locale configuration with fallback logic.
 *
 * @example
 * getLocaleCode('us-en') // 'EN_US'
 * getLocaleCode('ca-en') // 'EN_US' (Canada English uses same as US)
 * getLocaleCode('ca-fr') // 'FR'    (Canada French)
 * getLocaleCode('de-de') // 'DE'
 * getLocaleCode('de-en') // 'EN_GB'
 * getLocaleCode('cn-zh') // 'ZH_HANS'
 */
export function getLocaleCode(locale: string): string {
  // Try to get from configuration first
  try {
    return localeConfig.getAlgoliaLocaleCode(locale);
  } catch {
    // Fallback to manual mapping if config lookup fails
    const [country, language] = locale.split('-');
    const countryLower = country.toLowerCase();
    const langLower = language.toLowerCase();

    // Handle English - US/CA use EN_US, everywhere else uses EN_GB
    if (langLower === 'en') {
      if (countryLower === 'us' || countryLower === 'ca') {
        return 'EN_US';
      }
      return 'EN_GB';
    }

    // Handle Chinese - always use ZH_HANS for simplified Chinese
    if (langLower === 'zh') {
      return 'ZH_HANS';
    }

    // Handle Traditional Chinese
    if (langLower === 'zht') {
      return 'ZH_HANT';
    }

    // Handle Japanese (URL slug is 'jp' but code is 'JA')
    if (langLower === 'jp') {
      return 'JA';
    }

    // All other languages use just the uppercase language code
    return language.toUpperCase();
  }
}

/**
 * Get all candidate Algolia locale codes for a router locale, in priority order.
 *
 * Localized facet attributes (case, dial_color, type, material, …) are keyed in
 * the index by a language-region code. The brand's locale config may select a
 * different English variant than the index actually stores — e.g. the Gallet
 * config maps `us-en` to `EN_GB`, but the (currently Breitling) index keys
 * English watch facets under `EN_US`. For English we therefore return every
 * known English variant so callers can use whichever the index has data for.
 *
 * For non-English locales there is no such ambiguity, so a single code is returned
 * and behaviour is unchanged.
 *
 * @example
 * getLocaleCodeCandidates('us-en') // ['EN_GB', 'EN_US', 'EN'] (configured code first)
 * getLocaleCodeCandidates('de-de') // ['DE']
 */
export function getLocaleCodeCandidates(locale: string): string[] {
  const primary = getLocaleCode(locale);
  const language = locale.split('-')[1]?.toLowerCase();

  if (language === 'en') {
    // Configured code first, then the other English variants as fallbacks.
    return Array.from(new Set([primary, 'EN_US', 'EN_GB', 'EN']));
  }

  return [primary];
}

export const ALGOLIA_CONFIG = {
  /**
   * Algolia Application ID.
   * For testing, uses breitling's app ID.
   */
  appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',

  /**
   * Algolia Search API Key (public, search-only).
   */
  apiKey: process.env.NEXT_PUBLIC_ALGOLIA_API_KEY || '',

  /**
   * Optional separate API key for server-side rendering.
   * Falls back to the public key if not set.
   */
  ssrApiKey: process.env.ALGOLIA_SSR_API_KEY || process.env.NEXT_PUBLIC_ALGOLIA_API_KEY || '',

  /**
   * Environment prefix for index names.
   * e.g., 'production', 'staging', 'development'
   */
  environment: process.env.ALGOLIA_ENVIRONMENT || 'production',

  /**
   * Get the product index name for a locale.
   *
   * @param locale - Locale string (e.g., 'us-en')
   * @returns Index name (e.g., 'PRE.PRODUCT.us-usd')
   */
  getProductIndex(locale: string): string {
    const channel = getChannelFromLocale(locale);
    return `${this.environment}.PRODUCT.${channel}`;
  },

  /**
   * Get the store index name.
   *
   * @returns Index name (e.g., 'production.STORE')
   */
  getStoreIndex(): string {
    return `${this.environment}.STORE`;
  },

  /**
   * Get the service center index name.
   *
   * @returns Index name (e.g., 'production.SERVICE_CENTER')
   */
  getServiceCenterIndex(): string {
    return `${this.environment}.SERVICE_CENTER`;
  },

  /**
   * Get the contact center index name.
   * Used for per-country contact information (phone, email, hours).
   *
   * @returns Index name (e.g., 'production.CONTACT_CENTER')
   */
  getContactCenterIndex(): string {
    return `${this.environment}.CONTACT_CENTER`;
  },

  // Search defaults
  /** Default number of hits per page */
  hitsPerPage: 50,

  /** Maximum facet values to return */
  maxFacetValues: 100,

  /** Enable click analytics for queryID tracking */
  clickAnalytics: true,
} as const;
